import "./index.scss"
import React from "react"
import SlickSlider from "react-slick"
import SliderCard from "../Cards/SliderCard"
import { filterPosts } from "./utils/filterPosts"
import { cardSelector } from "./utils/cardSelector"
import { useStaticQuery, graphql, Link } from "gatsby"
import { GatsbyImage, getImage, StaticImage } from "gatsby-plugin-image"

const selectDefaultBackground = contentType => {
  switch (contentType) {
    case "update":
      return <StaticImage src="../../images/updates-slider.jpg" />
      break

    case "resource":
      return <StaticImage src="../../images/default-resources-bg.jpg" />
      break

    default:
      break
  }
}

export default function DynamicSlider({ component }) {
  // = = = = = = = = = = = = =
  // GraphQL
  const postsData = useStaticQuery(graphql`
    query postsData {
      resource: allDatoCmsResource(sort: { fields: datePosted, order: DESC }) {
        nodes {
          id
          slug
          title
          introText
          thematicTags
          structuralTags
          reportSubtitle
          typeOfResource
          relatedProjects {
            title
          }
          relatedFocusAreas {
            title
          }
          featuredImage {
            gatsbyImageData(placeholder: BLURRED)
          }
        }
      }
      update: allDatoCmsUpdate(sort: { fields: datePosted, order: DESC }) {
        nodes {
          id
          introText
          thematicTags
          structuralTags
          relatedProjects {
            title
          }
          relatedFocusAreas {
            title
          }
          title
          slug
          datePosted(formatString: "MMMM DD, YYYY")
          featuredImage {
            gatsbyImageData(placeholder: BLURRED)
          }
        }
      }
      project: allDatoCmsProject {
        nodes {
          id
          introText
          title
          slug
          externalLink
          isExternalLink
          relatedFocusAreas {
            title
          }
          featuredImage {
            gatsbyImageData(placeholder: BLURRED)
          }
        }
      }
      fiscally_sponsored_project: allDatoCmsFiscallySponsoredProject {
        nodes {
          id
          featuredImage {
            gatsbyImageData
          }
          introText
          externalLink
          title
          structuralTags
        }
      }
      press: allDatoCmsPress(sort: { fields: date, order: DESC }) {
        nodes {
          id
          date(formatString: "MMMM DD, YYYY")
          headline
          externalLink
          media
          relatedResources {
            title
          }
          relatedProjects {
            title
          }
          relatedFocusAreas {
            title
          }
        }
      }
      affiliate: allDatoCmsAffiliate {
        nodes {
          id
          name
          locationText
          intro
          websiteLink
          thematicTags
          image {
            gatsbyImageData(placeholder: BLURRED)
          }
        }
      }
      job: allDatoCmsJob(sort: { fields: datePosted, order: DESC }) {
        nodes {
          id
          title
          slug
          thematicTags
        }
      }
    }
  `)

  // = = = = = = = =
  // Data

  const {
    skip,
    limit,
    headline,
    tagToShow,
    cardColor,
    introduction,
    cardAlignment,
    showIntroText,
    relatedProjects,
    relatedResources,
    relatedFocusAreas,
  } = component

  const { contentType } = component?.contentType[0]

  const { hasBackgroundImage, backgroundColor, image } =
    component?.background[0]

  // = = = = = = = =
  // Utils

  const posts = postsData[contentType]?.nodes
  // Filter Posts depending on tag and skip props
  const filteredPosts = filterPosts(
    posts,
    relatedFocusAreas,
    relatedProjects,
    relatedResources,
    tagToShow,
    limit,
    skip
  )

  const bgImage = hasBackgroundImage ? getImage(image) : null

  // = = = = = = = =
  // Slider Utils

  const sliderSettings = {
    dots: true,
    arrows: false,
    speed: 1100,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: false,
    infinite: false,
    variableWidth: true,
    focusOnSelect: true,
    appendDots: dots => (
      <>
        <div className={`dots ${"_" + cardColor.replace("#", "")}`}>
          <ul>{dots}</ul>
        </div>
      </>
    ),
    responsive: [
      {
        breakpoint: 800,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  }

  return (
    <>
      {filteredPosts.length > 0 && (
        <section
          data-datocms-noindex
          className={`slider-section ${contentType}`}
          style={{
            backgroundColor:
              hasBackgroundImage === false ? backgroundColor : "#fff",
          }}
        >
          {hasBackgroundImage && bgImage && (
            <div className="slider-section--bgImage">
              <GatsbyImage image={bgImage} alt="" />
            </div>
          )}
          {!bgImage && (
            <div className="slider-section--bgImage">
              {selectDefaultBackground(contentType)}
            </div>
          )}

          {showIntroText ? (
            <div className="container slider-section__intro">
              <div className="row intro-row">
                <div className="col-lg-4">
                  <h3
                    className="slider-section__headline oblique text-white"
                    dangerouslySetInnerHTML={{ __html: headline }}
                  ></h3>
                  <p className="slider-section__intro text-white">{introduction}</p>
                </div>

                <div className="col-lg-8">
                  <div className="basicSliderWrapper">
                    <SlickSlider {...sliderSettings}>
                      {filteredPosts?.map(postData => (
                        <SliderCard
                          key={postData.title}
                          contentType={contentType}
                          postData={postData}
                          cardColor={cardColor}
                          cardAlignment={cardAlignment}
                        />
                      ))}
                    </SlickSlider>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className="container slider-section__intro">
                <div className="row">
                  <div className="col-6">
                    <h3
                      className="slider-section__headline oblique text-white"
                      dangerouslySetInnerHTML={{ __html: headline }}
                    ></h3>
                  </div>

                  {component?.link[0] && (
                    <div className="col-6">
                      <Link
                        className={`slider-section__link text-white`}
                        to={component?.link[0].linkUrl}
                      >
                        {component?.link[0].label}
                      </Link>
                    </div>
                  )}
                </div>
              </div>

              <div className="container">
                <div className="row">
                  <div className="basicSliderWrapper">
                    {filteredPosts && (
                      <SlickSlider {...sliderSettings}>
                        {cardSelector(
                          contentType,
                          filteredPosts,
                          cardColor,
                          cardAlignment
                        )}
                      </SlickSlider>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </section>
      )}
    </>
  )
}
